import { API_HOST, MODERATION_PATH } from './config'
import { fetchWithTimeout } from './support'
import type { ApiEnvelope } from './types'

/**
 * Teacher moderation calls.
 *
 * These go to our API rather than to LiveKit: muting someone else needs room
 * admin authority, which lives on the server with our API secret. The server
 * re-checks from the join code that the caller really is this class's teacher —
 * see Meetings::Moderation.
 *
 * Every action here is one-directional: OFF. There is deliberately no server
 * call that switches a student's microphone or camera back on. Bringing someone
 * back is a REQUEST the student accepts (see useClassroomSignals), because an
 * adult silently opening a child's camera is not something this app should be
 * able to do.
 */

// remove: meetings only (the server refuses it for classes).
export type ModerationAction = 'mute_audio' | 'mute_video' | 'stop_share' | 'remove'

const REQUEST_TIMEOUT_MS = 10_000

export async function moderate(
  credential: { code?: string | null; reconnectToken?: string | null },
  targetIdentity: string,
  action: ModerationAction,
): Promise<void> {
  const response = await fetchWithTimeout(
    `${API_HOST}${MODERATION_PATH}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      cache: 'no-store',
      body: JSON.stringify({
        code: credential.code ?? undefined,
        reconnect_token: credential.reconnectToken ?? undefined,
        target_identity: targetIdentity,
        // Not `action`: Everhall API reserves that parameter name for the controller
        // action, and it would be silently overwritten in params.
        moderation_action: action,
      }),
    },
    REQUEST_TIMEOUT_MS,
  )

  let envelope: ApiEnvelope<unknown> | undefined
  try {
    envelope = (await response.json()) as ApiEnvelope<unknown>
  } catch {
    /* classified by status below */
  }

  // Surfaced, unlike diagnostics: a teacher who taps "mute" and gets silence
  // cannot tell whether the class still hears the student.
  if (!response.ok || envelope?.status !== 'success') {
    throw new Error(envelope?.message ?? 'That did not work — try again')
  }
}
